import { z } from "zod";
import {
  CommercialisationMeetingType,
  CommercialisationMeetingTypeValues,
  CommercialisationTaskStatus,
  CommercialisationTaskStatusValues,
  CommercialisationTaskTargetType,
  CommercialisationTaskTargetTypeValues,
  CommercialisationTaskTemperatureTypeValues,
  CommercialisationTemperatureType,
} from "@ac-expo/api-types";
import { GenderType } from "@prisma/client";
import { isBefore } from "date-fns";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toDatabaseFormat } from "@ac-expo/utils";

const requireNamedString = "Le nom est requis.";
const invalidDateString = "La date de fin ne peut être avant celle du début.";

export const createCommercialisationCommentSchema = z.object({
  text: z.string().min(1, "Le commentaire ne doit pas être vide"),
});

export const commercialisationTaskMeetingSchema = z
  .object({
    date: z.string(),
    beginningHour: z.string(),
    endHour: z.string(),
    meetingType: z.enum(CommercialisationMeetingTypeValues),
    taskId: z.string(),
    meetingId: z.string().nullable(),
    targetId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const creating = !data.meetingId;
    if (creating && data.meetingId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ne pas fournir d'ID lors de la création",
        path: ["meetingId"],
      });
    }
    if (!creating && data.meetingId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'ID du rendez-vous est requis pour la mise à jour",
        path: ["meetingId"],
      });
    }
    if (isBefore(new Date(data.date + " " + data.beginningHour), new Date())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["date"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["beginningHour"],
      });
    }
    if (
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
  });

export const commercialisationBasketMeetingSchema = z
  .object({
    date: z.string(),
    beginningHour: z.string(),
    endHour: z.string(),
    meetingType: z.enum(CommercialisationMeetingTypeValues),
    basketId: z.string(),
    meetingId: z.string().nullable(),
    targetId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const creating = !data.meetingId;
    if (creating && data.meetingId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ne pas fournir d'ID lors de la création",
        path: ["meetingId"],
      });
    }
    if (!creating && data.meetingId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'ID du rendez-vous est requis pour la mise à jour",
        path: ["meetingId"],
      });
    }
    if (isBefore(new Date(data.date + " " + data.beginningHour), new Date())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["date"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["beginningHour"],
      });
    }
    if (
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
  });

export const createCommercialisationTaskSchema = z.object({
  title: z.string().min(1, "Le titre est requis"),
  date: z.date(),
  status: z.enum(CommercialisationTaskStatusValues),
  targetType: z.enum(CommercialisationTaskTargetTypeValues),
  description: z.string().optional(),
  assigneeId: z.string().optional(),
  organiserId: z.string().optional(),
  eventId: z.string().optional(),
  organiserEmail: z.string().optional(),
  organiserPhone: z.string().optional(),
  otherName: z.string().optional(),
  adoptedSolution: z.string().optional(),
});

export const updateCommercialisationTaskTargetSchema = z.object({
  targetType: z.enum(CommercialisationTaskTargetTypeValues),
  organiserId: z.string().optional(),
  eventId: z.string().optional(),
  organiserEmail: z.string().optional(),
  organiserPhone: z.string().optional(),
  otherName: z.string().optional(),
});

export const updateCommercialisationTaskStatusSchema = z.object({
  status: z.enum(CommercialisationTaskStatusValues),
});

export const updateCommercialisationTaskAssigneeSchema = z.object({
  assigneeId: z.string(),
});

export const updateCommercialisationTaskOrganiserSchema = z.object({
  organiserId: z.string(),
});

export const updateCommercialisationTaskEventSchema = z.object({
  eventId: z.string(),
});

export const updateCommercialisationTaskTemperatureSchema = z.object({
  temperature: z.enum(CommercialisationTaskTemperatureTypeValues),
});

export const updateCommercialisationTaskTargetProvenanceSchema = z.object({
  provenance: z.string(),
});

export const updateCommercialisationTaskDeadlineSchema = z.object({
  date: z
    .date()
    .refine(
      (d) => !isBefore(d, new Date()),
      "La nouvelle échéance doit être dans le futur."
    ),
});

export const createCommercialisationOrganiserEventSchema = z
  .object({
    name: z.string().min(1, requireNamedString),
    startDate: z.date(),
    endDate: z.date(),
    exhibitorCount: z.coerce.number(),
    visitorCount: z.coerce.number(),
    animationCount: z.coerce.number(),
    ticketingAmount: z.coerce.number(),
    adoptedSolution: z.string(),
  })
  .superRefine((data, ctx) => {
    if (isBefore(new Date(data.endDate), new Date(data.startDate))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidDateString,
        path: ["startDate"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidDateString,
        path: ["endDate"],
      });
    }
  });

export const updateCommercialisationTaskEventStatisticsSchema = z.object({
  exhibitorCount: z.coerce.number(),
  visitorCount: z.coerce.number(),
  animationCount: z.coerce.number(),
  ticketingAmount: z.coerce.number(),
});

export const updateCommercialisationOrganiserEventSchema = z
  .object({
    name: z.string().min(1, requireNamedString),
    startDate: z.date(),
    endDate: z.date(),
    exhibitorCount: z.coerce.number(),
    visitorCount: z.coerce.number(),
    animationCount: z.coerce.number(),
    ticketingAmount: z.coerce.number(),
    adoptedSolution: z.string(),
  })
  .superRefine((data, ctx) => {
    if (isBefore(new Date(data.endDate), new Date(data.startDate))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidDateString,
        path: ["startDate"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidDateString,
        path: ["endDate"],
      });
    }
  });

const upsertCommercialisationTaskInterlocutorSchema = z.object({
  name: z.string().min(1, requireNamedString),
  email: z.string().email("L'email est invalide"),
  phone: z
    .string()
    .refine(
      (value) => value === "" || isValidPhoneNumber(value, "FR"),
      "Numéro de téléphone invalide"
    ),
  function: z.string().min(1, "La fonction est requise"),
  adoptedSolution: z.string().optional(),
  text: z.string().optional(),
  gender: z.nativeEnum(GenderType).optional(),
});

export const createCommercialisationTaskInterlocutorSchema =
  upsertCommercialisationTaskInterlocutorSchema;

export const updateCommercialisationTaskInterlocutorSchema =
  upsertCommercialisationTaskInterlocutorSchema;

export type CreateCommercialisationTaskForm = z.infer<
  typeof createCommercialisationTaskSchema
>;
export type UpdateCommercialisationTaskTargetForm = z.infer<
  typeof updateCommercialisationTaskTargetSchema
>;
export type CreateCommercialisationCommentForm = z.infer<
  typeof createCommercialisationCommentSchema
>;
export type UpdateCommercialisationTaskStatusForm = z.infer<
  typeof updateCommercialisationTaskStatusSchema
>;
export type UpdateCommercialisationTaskAssigneeForm = z.infer<
  typeof updateCommercialisationTaskAssigneeSchema
>;
export type UpdateCommercialisationTaskOrganiserForm = z.infer<
  typeof updateCommercialisationTaskOrganiserSchema
>;
export type CommercialisationTaskMeetingForm = z.infer<
  typeof commercialisationTaskMeetingSchema
>;

export type CommercialisationBasketMeetingForm = z.infer<
  typeof commercialisationBasketMeetingSchema
>;

export type CancelCommercialisationTaskMeeting = {
  taskId: string;
  meetingId: string;
};

export type CancelCommercialisationBasketMeeting = {
  basketId: string;
  meetingId: string;
};

export type UpdateCommercialisationTaskEventForm = z.infer<
  typeof updateCommercialisationTaskEventSchema
>;
export type UpdateCommercialisationTaskTemperatureForm = z.infer<
  typeof updateCommercialisationTaskTemperatureSchema
>;
export type UpdateCommercialisationTaskTargetProvenanceForm = z.infer<
  typeof updateCommercialisationTaskTargetProvenanceSchema
>;
export type UpdateCommercialisationTaskDeadlineForm = z.infer<
  typeof updateCommercialisationTaskDeadlineSchema
>;

export type CreateCommercialisationOrganiserEventForm = z.infer<
  typeof createCommercialisationOrganiserEventSchema
>;

export type UpdateCommercialisationTaskEventStatisticsForm = z.infer<
  typeof updateCommercialisationTaskEventStatisticsSchema
>;

export type UpdateCommercialisationOrganiserEventForm = z.infer<
  typeof updateCommercialisationOrganiserEventSchema
>;

export type CreateCommercialisationTaskInterlocutorForm = z.infer<
  typeof createCommercialisationTaskInterlocutorSchema
>;

export type UpdateCommercialisationTaskInterlocutorForm = z.infer<
  typeof updateCommercialisationTaskInterlocutorSchema
>;

export const defaultCreateCommercialisationTaskValues = {
  title: "",
  date: new Date(),
  status: CommercialisationTaskStatus.ACTIVE,
  targetType: CommercialisationTaskTargetType.EVENT,
  description: "",
  organiserId: "",
  assigneeId: "",
  eventId: "",
  otherName: "",
  adoptedSolution: "",
} satisfies Partial<CreateCommercialisationTaskForm>;

export const defaultUpdateCommercialisationTaskTargetValues = {
  targetType: CommercialisationTaskTargetType.EVENT,
  organiserId: "",
  eventId: "",
  organiserEmail: "",
  organiserPhone: "",
  otherName: "",
} satisfies Partial<UpdateCommercialisationTaskTargetForm>;

export const defaultCreateCommercialisationCommentValues = {
  text: "",
} satisfies Partial<CreateCommercialisationCommentForm>;

export const defaultUpdateCommercialisationTaskStatusValues = {
  status: CommercialisationTaskStatus.ACTIVE,
} satisfies Partial<UpdateCommercialisationTaskStatusForm>;

export const defaultUpdateCommercialisationTaskAssigneeValues = {
  assigneeId: "",
} satisfies Partial<UpdateCommercialisationTaskAssigneeForm>;

export const defaultCommercialisationTaskMeetingValues = {
  date: toDatabaseFormat(new Date()),
  beginningHour: "9:00",
  endHour: "20:00",
  meetingType: CommercialisationMeetingType.PHONE,
  taskId: "",
  meetingId: null,
  targetId: "",
} satisfies Partial<CommercialisationTaskMeetingForm>;

export const defaultCommercialisationBasketMeetingValues = {
  date: toDatabaseFormat(new Date()),
  beginningHour: "9:00",
  endHour: "20:00",
  meetingType: CommercialisationMeetingType.PHONE,
  basketId: "",
  meetingId: null,
  targetId: "",
} satisfies Partial<CommercialisationBasketMeetingForm>;

export const defaultUpdateCommercialisationTaskEventValues = {
  eventId: "",
} satisfies Partial<UpdateCommercialisationTaskEventForm>;

export const defaultUpdateCommercialisationTaskTemperatureValues = {
  temperature: CommercialisationTemperatureType.LUKEWARM,
} satisfies Partial<UpdateCommercialisationTaskTemperatureForm>;

export const defaultUpdateCommercialisationTaskTargetProvenanceValues = {
  provenance: "",
} satisfies Partial<UpdateCommercialisationTaskTargetProvenanceForm>;

export const defaultUpdateCommercialisationTaskDeadlineValues = {
  date: new Date(),
} satisfies Partial<UpdateCommercialisationTaskDeadlineForm>;

export const defaultCreateCommercialisationOrganiserEventValues = {
  name: "",
  startDate: new Date(),
  endDate: new Date(),
  exhibitorCount: 0,
  visitorCount: 0,
  animationCount: 0,
  ticketingAmount: 0,
  adoptedSolution: "",
};

export const defaultUpdateCommercialisationOrganiserEventValues = {
  name: "",
  startDate: new Date(),
  endDate: new Date(),
  exhibitorCount: 0,
  visitorCount: 0,
  animationCount: 0,
  ticketingAmount: 0,
  adoptedSolution: "",
};

const defaultCommercialisationTaskInterlocutorValues = {
  name: "",
  email: "",
  phone: "",
  function: "",
  adoptedSolution: "",
  text: "",
  gender: undefined,
};

export const defaultCreateCommercialisationTaskInterlocutorValues =
  defaultCommercialisationTaskInterlocutorValues;

export const defaultUpdateCommercialisationTaskInterlocutorValues =
  defaultCommercialisationTaskInterlocutorValues;

export const defaultUpdateCommercialisationTaskEventStatisticsValues: UpdateCommercialisationTaskEventStatisticsForm =
  {
    animationCount: 0,
    exhibitorCount: 0,
    ticketingAmount: 0,
    visitorCount: 0,
  };
